import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useNotificationStore from '../../store/notificationStore';
import emergencyCatImage from '../../assets/images/emergency_cat.jpg';

// --- 애니메이션 정의 (기존 동일) ---
const emergencyRise = keyframes`
  0% { transform: translateY(500px); opacity: 0; }
  60% { transform: translateY(-20px); opacity: 1; }
  80% { transform: translateY(10px); }
  100% { transform: translateY(0); opacity: 1; }
`;

const popDown = keyframes`
  0% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(500px); opacity: 0; }
`;

const emergencyShake = keyframes`
  0% { transform: translate(1px, 1px) rotate(0deg); }
  10% { transform: translate(-1px, -2px) rotate(-1deg); }
  20% { transform: translate(-3px, 0px) rotate(1deg); }
  30% { transform: translate(3px, 2px) rotate(0deg); }
  40% { transform: translate(1px, -1px) rotate(1deg); }
  50% { transform: translate(-1px, 2px) rotate(-1deg); }
  60% { transform: translate(-3px, 1px) rotate(0deg); }
  70% { transform: translate(3px, 1px) rotate(-1deg); }
  80% { transform: translate(-1px, -1px) rotate(1deg); }
  90% { transform: translate(1px, 2px) rotate(0deg); }
  100% { transform: translate(1px, -2px) rotate(-1deg); }
`;

// --- 스타일 컴포넌트 ---
const Wrapper = styled.div<{ isVisible: boolean }>`
  position: fixed;
  bottom: 0;
  right: 2rem;
  z-index: 9990;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;

  ${props => props.isVisible
          ? css`
        animation: ${emergencyRise} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        visibility: visible;
      `
          : css`
        animation: ${popDown} 0.5s ease-in forwards;
      `
  }
`;

const SpeechBubble = styled.div`
  background: white;
  border: 3px solid #ff4d4f;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: -20px;
  margin-right: 40px;
  max-width: 280px;
  min-width: 220px;
  box-shadow: 4px 4px 0px rgba(0,0,0,0.2);
  position: relative;
  pointer-events: auto;
  z-index: 10;

  &::after {
    content: ''; position: absolute; bottom: -12px; right: 40px;
    border-width: 12px 12px 0; border-style: solid; border-color: #ff4d4f transparent; display: block; width: 0;
  }
  &::before {
    content: ''; position: absolute; bottom: -6px; right: 43px;
    border-width: 9px 9px 0; border-style: solid; border-color: white transparent; display: block; width: 0; z-index: 1;
  }
`;

const Title = styled.div`
  font-weight: 900;
  color: #ff4d4f;
  margin-bottom: 0.25rem;
  font-size: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Message = styled.div`
  color: #333;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 0.8rem;
  max-height: 300px;
  overflow-y: auto;
  word-break: break-word;

  /* Tiptap HTML 요소 스타일 강제 지정 */
  p { margin: 0 0 8px 0; }
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 4px 0;
    display: block;
  }
  strong { font-weight: bold; }
  em { font-style: italic; }
`;

// 버튼 그룹 컨테이너
const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const DismissButton = styled.button`
  background-color: #ff4d4f;
  color: white; border: none; border-radius: 4px; padding: 6px 12px;
  font-size: 0.8rem; font-weight: bold; cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  &:hover { background-color: #d9363e; }
  &:active { transform: translateY(1px); }
`;

// ✨ [추가] 모두 닫기 버튼 (회색 계열로 덜 강조)
const ClearAllButton = styled.button`
  background-color: #f0f0f0;
  color: #666;
  border: 1px solid #ccc;
  border-radius: 4px; padding: 6px 10px;
  font-size: 0.75rem; font-weight: bold; cursor: pointer;
  box-shadow: 0 2px 2px rgba(0,0,0,0.1);
  &:hover { background-color: #e0e0e0; color: #333; }
`;

const QueueBadge = styled.span`
  background-color: #333; color: white; font-size: 0.7rem; padding: 2px 6px; border-radius: 10px;
`;

const CatContainer = styled.div`
  width: 200px;
  height: auto;
  position: relative;
  overflow: visible;
  margin-bottom: -10px;
`;

const EmergencyCatImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  animation: ${emergencyShake} 0.1s linear infinite; 
`;

const MascotNotification = () => {
    // ✨ clearQueue 가져오기
    const { currentNotification, dismissCurrent, clearQueue, queue } = useNotificationStore();

    const [renderData, setRenderData] = useState(currentNotification);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (currentNotification) {
            setRenderData(currentNotification);
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    }, [currentNotification]);

    if (!renderData && !currentNotification) return null;

    return (
        <Wrapper isVisible={isVisible}>
            {renderData && (
                <SpeechBubble key={renderData.id}>
                    <Title>
                        🚨 {renderData.title} 🚨
                        {queue.length > 0 && <QueueBadge>+{queue.length}</QueueBadge>}
                    </Title>
                    <Message
                        dangerouslySetInnerHTML={{ __html: renderData.content }}
                    />

                    <ButtonGroup>
                        {/* ✨ 큐에 쌓인 게 있을 때만 '모두 닫기' 버튼 노출 */}
                        {queue.length > 0 && (
                            <ClearAllButton onClick={clearQueue}>
                                모두 닫기
                            </ClearAllButton>
                        )}
                        <DismissButton onClick={dismissCurrent}>
                            {queue.length > 0 ? '다음!' : '확인'}
                        </DismissButton>
                    </ButtonGroup>
                </SpeechBubble>
            )}

            <CatContainer>
                <EmergencyCatImg src={emergencyCatImage} alt="긴급상황 고양이" />
            </CatContainer>
        </Wrapper>
    );
};

export default MascotNotification;